import { v4 } from "uuid";
import type { IConfig, Timeout } from "whole-shared";
import { logger } from "whole-shared";

import { Player } from "./db";

export interface Cleaner {
  clean: () => void;
  push: (cleaner: () => void) => void;
}

export const createCleaner = (): Cleaner => {
  const cleaners = new Set<() => void>();

  return {
    clean: () => {
      for(const cleaner of cleaners) cleaner();
    },
    push: (cleaner: () => void) => cleaners.add(cleaner)
  };
};

/** EventTarget helper. */
export class EventEmitter<T extends { [k: string]: unknown[] }> extends EventTarget {
  private cleaners = new Map<() => void, undefined>();

  /** Removes all the attached listeners. */
  clear() {
    for(const cleaner of this.cleaners.keys()) cleaner();

    this.cleaners.clear();
  }

  /** Emits an event. */
  emit<E extends keyof T>(event: E, ...args: T[E]) {
    this.dispatchEvent(new CustomEvent<T[E]>(event as string, { detail: args }));
  }

  /**
   * Attaches a listener to an event.
   *
   * @returns the callback to call to detach the same listener.
   */
  on<E extends keyof T>(event: E, cleaner: Cleaner | ((...args: T[E]) => void), listener?: (...args: T[E]) => void) {
    let clean: Cleaner | undefined;

    if(! listener) {
      if(typeof cleaner === "function") listener = cleaner;
      else throw new Error("Bad call");
    } else if(typeof cleaner === "object") clean = cleaner;
    else throw new Error("Bad call");

    const handler = (({ detail }: CustomEvent<T[E]>) => listener(...detail)) as (event: Event) => void;

    const c = () => {
      this.removeEventListener(event as string, handler);
      this.cleaners.delete(c);
    };

    this.addEventListener(event as string, handler);
    this.cleaners.set(c, undefined);

    if(clean) clean.push(c);

    return c;
  }
}

export interface IClientConfig extends Omit<IConfig, "player"> {
  clientId: string;
  player: Player;
}

class Config extends EventEmitter<{ config: [config: IClientConfig] }> {
  private clientId: string;
  private state = "";
  private timeOut?: Timeout;

  public constructor() {
    super();

    const clientId = sessionStorage.getItem("clientId");

    if(clientId) this.clientId = clientId;
    else sessionStorage.setItem("clientId", (this.clientId = v4()));

    this.config();
  }

  private config() {
    let delay = 600000;

    (async () => {
      this.timeOut = undefined;

      const res = await fetch("/auth/config");
      const state = (await res.json()) as IConfig;
      const newState = JSON.stringify(state);

      if(newState === this.state) return;

      this.state = newState;
      this.emit("config", { clientId: this.clientId, player: new Player(state.player), stage: state.stage });
    })()
      .catch(error => {
        logger.error("Config error", error);
        delay = 1000;
      })
      .finally(() => {
        if(this.timeOut) clearTimeout(this.timeOut);
        this.timeOut = setTimeout(() => this.config(), delay);
      });
  }
}

export const config = new Config();
